import { Item, ProductionTask_Input, Product_Output } from "@/api";
import { formatDate, formatNumber } from "@/utils/format";

interface EchoProductionTaskDataTableProps {
  items: Item[];
  tasks: ProductionTask_Input[];
  productsById: Record<string, Product_Output>;
  selectedTaskId: string | null;
  onSelectTask: (taskId: string | null, orderIds: string[]) => void;
}

const EchoProductionTaskDataTable = ({
  items,
  tasks,
  productsById,
  selectedTaskId,
  onSelectTask,
}: EchoProductionTaskDataTableProps) => {
  const selectedIds = new Set(items.map((i) => i.品目ID));

  const rows = tasks
    .filter((task) => selectedIds.has(task.sku_id))
    .sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      const machineCompare = (a.machine_id ?? "").localeCompare(b.machine_id ?? "");
      if (machineCompare !== 0) return machineCompare;
      return a.sku_id.localeCompare(b.sku_id);
    });

  if (rows.length === 0) {
    return <div className="text-sm text-gray-500">選択中の品目に対応する成形がありません</div>;
  }

  const totalQty = rows.reduce((sum, task) => sum + (task.quantity ?? 0), 0);
  const uniqueMachines = new Set(rows.map((task) => task.machine_id)).size;
  const fixedCount = rows.filter((task) => task.is_fixed).length;
  const totalDuration = rows.reduce((sum, task) => sum + (task.duration ?? 0), 0) / 60 / 60;

  return (
    <div className="flex flex-column gap-3">
      <div className="text-sm text-gray-700">
        タスク数: {rows.length} / 成形数量合計: {totalQty} / 設備数: {uniqueMachines} / 固定タスク: {fixedCount} / 成形時間合計(h): {totalDuration.toFixed(2)}
      </div>
      <div className="overflow-auto">
        <table className="w-full table-auto text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="p-2 text-left">品目ID</th>
              <th className="p-2 text-left">品名</th>
              <th className="p-2 text-left">設備ID</th>
              <th className="p-2 text-left">生産日</th>
              <th className="p-2 text-right">数量</th>
              <th className="p-2 text-right">良品数</th>
              <th className="p-2 text-right">不良数</th>
              <th className="p-2 text-right">成形時間(h)</th>
              <th className="p-2 text-left">固定</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((task) => {
              const item = items.find((i) => i.品目ID === task.sku_id);
              const defectUnits = task.quantity_total - task.quantity;
              const goodUnits = task.quantity ?? 0;
              const duration = (task.duration ?? 0) / 60 / 60;
              const orderIds = Array.isArray(task.order_id)
                ? task.order_id
                : task.order_id
                  ? [task.order_id]
                  : [];
              const isSelected = selectedTaskId === task.task_id;

              return (
                <tr
                  key={task.task_id}
                  className={`odd:bg-white even:bg-gray-50 ${isSelected ? "row-highlight" : ""} cursor-pointer`}
                  onClick={() => {
                    if (isSelected) {
                      onSelectTask(null, []);
                      return;
                    }
                    onSelectTask(task.task_id, orderIds);
                  }}
                >
                  <td className="p-2 whitespace-nowrap">{task.sku_id}</td>
                  <td className="p-2">{item?.製造分類 ?? ""}</td>
                  <td className="p-2 whitespace-nowrap">{task.machine_id}</td>
                  <td className="p-2 whitespace-nowrap">{formatDate(task.date)}</td>
                  <td className="p-2 text-right">{formatNumber(task.quantity_total)}</td>
                  <td className="p-2 text-right">{formatNumber(goodUnits)}</td>
                  <td className="p-2 text-right">{formatNumber(defectUnits)}</td>
                  <td className="p-2 text-right">{formatNumber(duration)}</td>
                  <td className="p-2 whitespace-nowrap">{task.is_fixed ? "固定" : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EchoProductionTaskDataTable;
